// src/components/ProductsBySubcategory.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Stack,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";

import { GetProducts } from "../utils/product";
import { GetCategories } from "../utils/categoryAPI"; // ⚠️ עדכני נתיב אם אצלך שונה
import SingleProductDesktop from "./SingleProductDesktop";

export default function ProductsBySubcategory() {
  const { subId } = useParams(); // /products/sub/:subId
  const navigate = useNavigate();
  const subIdNum = Number(subId);

  const [products, setProducts] = useState([]);
  const [prodLoading, setProdLoading] = useState(false);
  const [prodError, setProdError] = useState("");

  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);

  // --------- Load categories once ----------
  useEffect(() => {
    let mounted = true;
    setCatLoading(true);

    GetCategories()
      .then((data) => {
        if (!mounted) return;
        setCategories(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        console.error(e);
        if (!mounted) return;
        setCategories([]);
      })
      .finally(() => mounted && setCatLoading(false));

    return () => {
      mounted = false;
    };
  }, []);

  // --------- Helpers ----------
  const getChildrenOf = (parentId) =>
    categories.filter((c) => Number(c.parentCategoryId) === Number(parentId));

  const currentCat = useMemo(
    () => categories.find((c) => Number(c.id) === subIdNum),
    [categories, subIdNum]
  );

  // רמה 2 רלוונטית: אם אנחנו על רמה 3 -> ההורה; אם על רמה 2 -> היא עצמה
  // const level2Id = useMemo(() => {
  //   if (!currentCat) return subIdNum;

  //   const children = getChildrenOf(currentCat.id);
  //   if (children.length > 0) return Number(currentCat.id); // רמה 2

  //   return Number(currentCat.parentCategoryId ?? currentCat.id); // רמה 3 -> ההורה (רמה 2)
  // }, [currentCat, categories, subIdNum]);

  const level2Id = useMemo(() => {
  if (!currentCat) return subIdNum;

  const parent = categories.find((c) => Number(c.id) === Number(currentCat.parentCategoryId));

  // אם אין הורה בכלל -> זו קטגוריה ראשית, נשים אותה כבסיס
  if (!parent) return Number(currentCat.id);

  // אם ההורה הוא קטגוריה ראשית (אין לו הורה) -> currentCat היא רמה 2
  if (parent.parentCategoryId == null) return Number(currentCat.id);

  // אחרת -> currentCat היא רמה 3, ורמה 2 היא ההורה שלה
  return Number(parent.id);
}, [currentCat, categories, subIdNum]);


  const level2Cat = useMemo(
    () => categories.find((c) => Number(c.id) === Number(level2Id)),
    [categories, level2Id]
  );

  // צ'יפים של רמה 3 (תתי-תת) - הילדים של רמה 2
  const level3Chips = useMemo(() => {
    if (!level2Id) return [];
    // אם חשוב לך “סדר כמו בשרת” גם בצ’יפים, אל תמייני בכלל
    // אם חשוב לך UI מסודר, תשאירי את המיון לפי שם:
    return getChildrenOf(level2Id).sort((a, b) =>
      (a.nameCategor || "").localeCompare(b.nameCategor || "")
    );
  }, [categories, level2Id]);

  // מחזיר את כל ה-leaf-ים שמתחת לקטגוריה (כולל אם היא leaf בעצמה)
  const getLeafDescendants = (startId) => {
    const leaves = [];
    const stack = [Number(startId)];
    const seen = new Set();

    while (stack.length) {
      const id = stack.pop();
      if (seen.has(id)) continue;
      seen.add(id);

      const kids = getChildrenOf(id);
      if (!kids.length) {
        leaves.push(id);
      } else {
        kids.forEach((k) => stack.push(Number(k.id)));
      }
    }

    return Array.from(new Set(leaves));
  };

  // --------- Load products ----------
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setProdLoading(true);
        setProdError("");
        setProducts([]);

        // אם אין קטגוריות עדיין - fallback
        if (!categories.length) {
          const data = await GetProducts({ categoryId: subId });
          if (!mounted) return;
         setProducts(Array.isArray(data?.items) ? data.items : []);
          return;
        }

        const selected = categories.find((c) => Number(c.id) === subIdNum);
        if (!selected) {
          const data = await GetProducts({ categoryId: subId });
          if (!mounted) return;
       setProducts(Array.isArray(data?.items) ? data.items : []);
          return;
        }

        // 1) מביאים leaf-ים מתחת לקטגוריה שנבחרה
        // אם זו רמה 3 -> זה יחזיר רק אותה
        // אם זו רמה 2 -> זה יחזיר את כל תתי-התת שלה
        const leafIds = getLeafDescendants(selected.id).sort((a, b) => a - b);

        // 2) חשוב: לכלול גם מוצרים שמקושרים ישירות לרמה 2 (אם קיימים),
        // וגם לשמור סדר יציב של "השרת" לפי סדר הקריאות:
        // קודם selected.id, אחר כך leafIds
        const idsToFetch = [];
        const seenIds = new Set();
        const addId = (id) => {
          const n = Number(id);
          if (!seenIds.has(n)) {
            seenIds.add(n);
            idsToFetch.push(n);
          }
        };

        addId(selected.id);
        leafIds.forEach(addId);

        // 3) Promise.all מחזיר לפי סדר idsToFetch.
        // בתוך כל קבוצה – הסדר הוא בדיוק מה שהשרת החזיר.
        const results = await Promise.all(
     idsToFetch.map(async (id) => {
  const res = await GetProducts({ categoryId: id, page: 1, pageSize: 100 });
  return res?.items ?? [];
})
        );

        const merged = results.flat().filter(Boolean);

        // 4) הסרת כפילויות בלי לשבור סדר (שומר הופעה ראשונה)
        const uniqueById = Array.from(
          new Map(merged.map((p) => [p.id, p])).values()
        );

        if (!mounted) return;
        setProducts(uniqueById);
      } catch (e) {
        console.error(e);
        if (!mounted) return;
        setProdError("נכשלה טעינת מוצרים");
      } finally {
        mounted && setProdLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [subId, subIdNum, categories]);

  const goToCategory = (id) => {
    navigate(`/products/sub/${id}`);
  };

  return (
    <Container sx={{ py: 2 }} dir="rtl">
      {/* כותרת + חזרה */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
        mt={1}
      >
        <Typography variant="h6" fontWeight="bold">
          {level2Cat?.nameCategor ? `מוצרים - ${level2Cat.nameCategor}` : "מוצרים"}
        </Typography>
        <Button variant="text" onClick={() => navigate(-1)}>
          חזרה
        </Button>
      </Stack>

      {/* סרגל תתי-תת + כפתור "הכל" שתמיד מחזיר לרמה 2 */}
      {/* {!catLoading && level2Id ? (
        <>
          <Stack direction="row" spacing={1} sx={{ overflowX: "auto", py: 1 }}>
            <Chip
              label="הכל"
              clickable
              onClick={() => goToCategory(level2Id)}
              variant={Number(subIdNum) === Number(level2Id) ? "filled" : "outlined"}
            />

            {level3Chips.map((c) => (
              <Chip
                key={c.id}
                label={c.nameCategor}
                clickable
                onClick={() => goToCategory(c.id)}
                variant={Number(c.id) === Number(subIdNum) ? "filled" : "outlined"}
              />
            ))}
          </Stack>
          <Divider sx={{ mb: 2 }} />
        </>
      ) : null} */}

      {!catLoading && level2Id ? (
  <>
    <Stack direction="row" spacing={1} sx={{ overflowX: "auto", py: 1 }}>
      <Chip
        label="הכל"
        clickable
        onClick={() => goToCategory(level2Id)}
        variant={Number(subIdNum) === Number(level2Id) ? "filled" : "outlined"}
      />

      {/* רק אם יש תת-תת קטגוריות - נציג אותן */}
      {level3Chips.length > 0 &&
        level3Chips.map((c) => (
          <Chip
            key={c.id}
            label={c.nameCategor}
            clickable
            onClick={() => goToCategory(c.id)}
            variant={Number(c.id) === Number(subIdNum) ? "filled" : "outlined"}
          />
        ))}
    </Stack>

    {/* Divider רק אם באמת יש עוד צ'יפים חוץ מהכל */}
    {level3Chips.length > 0 && <Divider sx={{ mb: 2 }} />}
  </>
) : null}


      {/* מצבי טעינה / שגיאה / ריק / רשימה */}
      {prodLoading ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress />
        </Stack>
      ) : prodError ? (
        <Typography color="error">{prodError}</Typography>
      ) : products.length === 0 ? (
        <Typography color="text.secondary">אין מוצרים להצגה</Typography>
      ) : (
        <Grid container spacing={2}>
          {products.filter(Boolean)
  .map((product, idx) => (
    <Grid
      item
      key={product.id ?? product.ID ?? product.Id ?? `${product.name}-${product.categoryId}-${idx}`}
      xs={2}
      sm={4}
      md={3}
    >
              <SingleProductDesktop product={product} />
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}
